import { useState } from 'react';
import type { AnyEvent } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow } from './SheetShell.tsx';
import { ConfirmDeletePanel } from './fields.tsx';
import { fmtTime, fmtDur } from '../../lib/format.ts';
import { summarise, summaryLabel } from '../../lib/breast.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  event: AnyEvent | null;
  onDelete: (event: AnyEvent) => Promise<unknown> | void;
  onEdit?: (event: AnyEvent) => void;
  /** Re-open a stopped durative session (sleep / breast feed / pump) by
   *  clearing ended_at. Only shown when applicable. */
  onResume?: (event: AnyEvent) => Promise<unknown> | void;
};

export function EventDetailSheet({ open, onClose, event, onDelete, onEdit, onResume }: Props) {
  const [confirming, setConfirming] = useState(false);

  if (!event) return null;

  const meta = describe(event);
  const at = event._t === 'diaper' ? event.occurred_at : event.started_at;
  const ended = event._t !== 'diaper' ? event.ended_at : null;
  const dur =
    ended != null && event._t !== 'diaper'
      ? Math.max(0, Math.floor((ended - event.started_at) / 60))
      : null;

  // Sessions that can be resumed: durative (sleep / breast / pump) that have
  // an end time set. Bottle / solid are point-in-time even if stored as a
  // feeding_session row — resuming them doesn't make semantic sense.
  const canResume =
    !!onResume &&
    ended != null &&
    (event._t === 'sleep' ||
      event._t === 'pump' ||
      (event._t === 'feed' && (event.type === 'breast_left' || event.type === 'breast_right')));

  return (
    <SheetShell
      open={open}
      onClose={() => {
        setConfirming(false);
        onClose();
      }}
      title={meta.label}
      tagline={meta.subtitle}
      accent={meta.accent}
      icon={(p) => <meta.Icn {...p} />}
      primaryLabel={confirming ? 'Yes, delete' : 'Delete'}
      primaryDanger
      secondaryLabel={confirming ? 'Keep' : 'Close'}
      onPrimary={async () => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        await onDelete(event);
        setConfirming(false);
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FieldRow label="Time" value={fmtTime(at)} hint={new Date(at * 1000).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })} />
        {dur != null && (
          <FieldRow label="Duration" value={fmtDur(dur)} hint={ended ? `ended ${fmtTime(ended)}` : ''} />
        )}
        {ended == null && event._t !== 'diaper' && (
          <FieldRow label="Status" value="In progress" hint="No end time yet" />
        )}
        {meta.fields.map(({ label, value, hint }) => (
          <FieldRow key={label} label={label} value={value} hint={hint} />
        ))}
        {canResume && !confirming && (
          <button
            type="button"
            className="lt-btn lt-btn-primary"
            style={{ width: '100%', padding: '11px 0', fontSize: 14, marginTop: 4 }}
            onClick={async () => {
              await onResume?.(event);
              onClose();
            }}
          >
            <Icon.play s={14} /> Resume this session
          </button>
        )}
        {onEdit && !confirming && (
          <button
            type="button"
            className="lt-btn lt-btn-soft"
            style={{ width: '100%', padding: '11px 0', fontSize: 14, marginTop: 4 }}
            onClick={() => onEdit(event)}
          >
            <Icon.edit s={14} /> Edit event
          </button>
        )}
        {confirming && (
          <ConfirmDeletePanel message="This is permanent. The event will disappear from the chart, stats, and feed." />
        )}
      </div>
    </SheetShell>
  );
}

function describe(e: AnyEvent) {
  if (e._t === 'sleep') {
    return {
      label: 'Sleep',
      subtitle: e.location ? `In ${e.location}` : undefined,
      accent: 'sleep-night',
      Icn: Icon.moon,
      fields: [
        ...(e.location ? [{ label: 'Location', value: e.location }] : []),
        ...(e.notes ? [{ label: 'Notes', value: e.notes }] : []),
      ] as { label: string; value: string; hint?: string }[],
    };
  }
  if (e._t === 'feed') {
    const isBreast = e.type === 'breast_left' || e.type === 'breast_right';
    let segmentFields: { label: string; value: string; hint?: string }[] = [];
    if (isBreast) {
      const s = summarise(e);
      if (s.hasHistory) {
        segmentFields.push({
          label: 'Per-side',
          value: summaryLabel(s, { showPaused: false }) || '—',
        });
        if (s.pausedSec >= 60) {
          segmentFields.push({
            label: 'Paused',
            value: fmtDur(Math.round(s.pausedSec / 60)),
            hint: 'time during this feed when neither breast was active',
          });
        }
      }
    }
    return {
      label: e.type === 'bottle' ? 'Bottle' : e.type === 'solid' ? 'Solid' : 'Breast feed',
      subtitle: undefined,
      accent: e.type === 'bottle' ? 'bottle' : e.type === 'solid' ? 'solid' : 'breast',
      Icn: e.type === 'bottle' ? Icon.bottle : e.type === 'solid' ? Icon.solid : Icon.breast,
      fields: [
        ...(e.amount_ml ? [{ label: 'Amount', value: `${e.amount_ml} ml` }] : []),
        ...(e.content ? [{ label: 'Contents', value: e.content }] : []),
        ...(isBreast
          ? [{ label: 'Last side', value: e.type === 'breast_left' ? 'Left' : 'Right' }]
          : []),
        ...segmentFields,
        ...(e.notes ? [{ label: 'Notes', value: e.notes }] : []),
      ] as { label: string; value: string; hint?: string }[],
    };
  }
  if (e._t === 'pump') {
    return {
      label: 'Pump',
      subtitle: e.side ? `${e.side} side` : undefined,
      accent: 'pump',
      Icn: Icon.pump,
      fields: [
        ...(e.side ? [{ label: 'Side', value: e.side }] : []),
        ...(e.yield_ml != null ? [{ label: 'Total yield', value: `${e.yield_ml} ml` }] : []),
        ...(e.yield_left_ml != null ? [{ label: 'Left', value: `${e.yield_left_ml} ml` }] : []),
        ...(e.yield_right_ml != null ? [{ label: 'Right', value: `${e.yield_right_ml} ml` }] : []),
        ...(e.notes ? [{ label: 'Notes', value: e.notes }] : []),
      ] as { label: string; value: string; hint?: string }[],
    };
  }
  // diaper
  return {
    label: `Diaper · ${e.kind}`,
    subtitle: undefined,
    accent: e.kind === 'pee' ? 'pee' : 'poop',
    Icn: Icon.diaper,
    fields: [
      { label: 'Kind', value: e.kind },
      ...(e.consistency ? [{ label: 'Consistency', value: e.consistency }] : []),
      ...(e.color ? [{ label: 'Colour', value: e.color }] : []),
      ...(e.leak ? [{ label: 'Leak', value: 'Yes' }] : []),
      ...(e.notes ? [{ label: 'Notes', value: e.notes }] : []),
    ] as { label: string; value: string; hint?: string }[],
  };
}
